import { logClientError } from '@utils/server-log';
import { Injectable } from "@angular/core";
import { SocialAuthProvider, User } from "@models/user";
import {
  AuthenticationDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { removeSymbol } from "src/util/util";
import { CognitoProvider } from "./classes/cognito-provider";

@Injectable({
  providedIn: "root",
})
export class CognitoService extends CognitoProvider {
  constructor() {
    super();
  }

  auth(username: string, password: string): Promise<any> {
    const authenticationDetails = new AuthenticationDetails({ Username: username, Password: password });
    this.cognitoUser = new CognitoUser({
      Username: username,
      Pool: this.userPool,
    });
    return new Promise((resolve, reject) => {
      this.cognitoUser?.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          localStorage.setItem("accessToken", result.getAccessToken().getJwtToken());
          localStorage.setItem("refreshToken", result.getRefreshToken().getToken());
          localStorage.setItem("idToken", result.getIdToken().getJwtToken());
          resolve(result);
        },
        onFailure: (err) => reject(this.getErrorMessage(err.code)),
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          this.userAttributes = userAttributes;
          this.requiredAttributes = requiredAttributes;
          reject("new_password_required");
        },
      });
    });
  }

  authWithProvider(provider: SocialAuthProvider): Promise<any> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: provider,
        Pool: this.userPool,
      });
      cognitoUser.authenticateUser(new AuthenticationDetails({ Username: provider }), {
        onSuccess: (result) => {
          localStorage.setItem("accessToken", result.getAccessToken().getJwtToken());
          localStorage.setItem("refreshToken", result.getRefreshToken().getToken());
          localStorage.setItem("idToken", result.getIdToken().getJwtToken());
          resolve(result);
        },
        onFailure: (err) => reject(this.getErrorMessage(err.code)),
      });
    });
  }

  async newPassword(newPassword: string): Promise<any> {
    try {
      return new Promise((resolve, reject) => {
        this.cognitoUser?.completeNewPasswordChallenge(newPassword, this.requiredAttributes, {
          onSuccess: (result) => {
            localStorage.setItem("accessToken", result.getAccessToken().getJwtToken());
            localStorage.setItem("refreshToken", result.getRefreshToken().getToken());
            localStorage.setItem("idToken", result.getIdToken().getJwtToken());
            resolve(result);
          },
          onFailure: (err) => {
            logClientError("Error during completeNewPasswordChallenge: ", err);
            reject(this.getErrorMessage(err.code));
          },
        });
      });
    } catch (err: any) {
      logClientError("Error during completeNewPasswordChallenge: ", err);
      throw this.getErrorMessage(err.code);
    }
  }

  signUp(user: User): Promise<any> {
    const attributeList = [
      new CognitoUserAttribute({ Name: "name", Value: user.name?.concat(" " + user?.surname).trim() ?? "" }),
      new CognitoUserAttribute({ Name: "given_name", Value: user.name ?? "" }),
      new CognitoUserAttribute({ Name: "family_name", Value: user?.surname ?? "" }),
      new CognitoUserAttribute({ Name: "email", Value: user.email ?? "" }),
      new CognitoUserAttribute({ Name: "phone_number", Value: "+55".concat(removeSymbol(user.phone ?? "")) ?? "" }),
    ];

    return new Promise((resolve, reject) => {
      this.userPool.signUp(user.email ?? "", user.password ?? "", attributeList, [], (err: any, result: any) => {
        if (err) {
          const errorMessage = err.code ? this.getErrorMessage(err.code) : err.message;
          reject(errorMessage);
        } else {
          resolve(result.user);
        }
      });
    });
  }

  refreshSession(): Promise<any> {
    const cognitoUser = this.getCurrentUser();
    if (!cognitoUser) {
      return Promise.reject("User is not authenticated");
    }
    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err: any, session: any) => {
        if (err) {
          reject(err);
        } else {
          localStorage.setItem("accessToken", session.getAccessToken().getJwtToken());
          localStorage.setItem("refreshToken", session.getRefreshToken().getToken());
          localStorage.setItem("idToken", session.getIdToken().getJwtToken());
          resolve(session);
        }
      });
    });
  }

  confirmRegistration(username: string, code: string): Promise<any> {
    const cognitoUser = new CognitoUser({ Username: username, Pool: this.userPool });
    return new Promise((resolve, reject) => {
      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          const errorMessage = err.code ? this.getErrorMessage(err.code) : err.message;
          reject(errorMessage);
        } else {
          resolve(result);
        }
      });
    });
  }

  resendConfirmationCode(email: string): Promise<any> {
    const cognitoUser = new CognitoUser({ Username: email, Pool: this.userPool });
    return new Promise((resolve, reject) => {
      cognitoUser.resendConfirmationCode((err: any, result) => {
        if (err) {
          const errorMessage = err.code ? this.getErrorMessage(err.code) : err.message;
          reject(errorMessage);
        } else {
          resolve(result);
        }
      });
    });
  }

  getUser(): Promise<CognitoUserAttribute[]> {
    return new Promise((resolve, reject) => {
      this.getCurrentUser()?.getUserAttributes((err: any, userAttributes: CognitoUserAttribute[] | undefined) => {
        if (err) {
          const errorMessage = err.code ? this.getErrorMessage(err.code) : err.message;
          reject(errorMessage);
        } else {
          if (userAttributes) {
            resolve(userAttributes);
          } else {
            reject("userAttributes is undefined");
          }
        }
      });
    });
  }

  updateUser(user: any): Promise<any> {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      return Promise.reject("User is not authenticated");
    }
    return this.ensureAuthenticated(currentUser).then(() => {
      const attributes = this.mapToCognitoUserAttribute(user);
      return new Promise((resolve, reject) => {
        currentUser.updateAttributes(attributes, (err: any, result) => {
          if (err) {
            const errorMessage = err.code ? this.getErrorMessage(err.code) : err.message;
            reject(errorMessage);
          } else {
            currentUser.getUserAttributes((err, updatedUserAttributes: CognitoUserAttribute[] | undefined) => {
              if (err) {
                reject(err.message);
              } else if (!updatedUserAttributes) {
                reject("CognitoUserAttribute[] is undefined");
              } else {
                if (updatedUserAttributes) {
                  resolve(updatedUserAttributes);
                } else {
                  reject("CognitoUserAttribute[] is undefined");
                }
              }
            });
          }
        });
      });
    });
  }

  changePassword(oldPassword: string, newPassword: string): Promise<any> {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      return Promise.reject("User is not authenticated");
    }
    return this.ensureAuthenticated(currentUser).then(() => {
      return new Promise((resolve, reject) => {
        currentUser.changePassword(oldPassword, newPassword, (err: any, result) => {
          if (err) {
            const errorMessage = err.code ? this.getErrorMessage(err.code) : err.message;
            reject(errorMessage);
          } else {
            resolve(result);
          }
        });
      });
    });
  }

  deleteUser(): Promise<any> {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      return Promise.reject("User is not authenticated");
    }
    return this.ensureAuthenticated(currentUser).then(() => {
      return new Promise((resolve, reject) => {
        currentUser.deleteUser((err: any, result) => {
          if (err) {
            const errorMessage = err.code ? this.getErrorMessage(err.code) : err.message;
            reject(errorMessage);
          } else {
            localStorage.clear();
            this.router.navigate(["/login"]);
            resolve(result);
          }
        });
      });
    });
  }

  logout(): Promise<void> {
    return new Promise((resolve, reject) => {
      const currentUser = this.getCurrentUser();
      if (!currentUser) {
        localStorage.clear();
        this.router.navigate(["/login"]);
        return reject("Nenhum usuário está logado no momento");
      }
      return this.ensureAuthenticated(currentUser)
        .then(() => {
          currentUser.signOut();
          localStorage.clear();
          this.router.navigate(["/login"]);
          resolve();
        })
        .catch(() => {
          localStorage.clear();
          this.router.navigate(["/login"]);
          reject("Nenhum usuário está logado no momento");
        });
    });
  }

  forgotPassword(email: string): Promise<any> {
    const cognitoUser = new CognitoUser({ Username: email, Pool: this.userPool });
    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: (result) => resolve(result),
        onFailure: (err: any) => {
          const errorMessage = err.code ? this.getErrorMessage(err.code) : err.message;
          reject(errorMessage);
        },
      });
    });
  }

  confirmPassword(email: string, newPassword: string, code: string): Promise<any> {
    if (!code || !newPassword || !email) {
      return Promise.reject("Código, nova senha e email são obrigatórios");
    }
    const cognitoUser = new CognitoUser({ Username: email, Pool: this.userPool });
    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: (result) => resolve(result),
        onFailure: (err: any) => {
          const errorMessage = err.code ? this.getErrorMessage(err.code) : err.message;
          reject(errorMessage);
        },
      });
    });
  }

  getCurrentUser(): CognitoUser | null {
    const cognitoUser = this.userPool.getCurrentUser();

    if (cognitoUser) {
      return cognitoUser;
    }

    const idToken = localStorage.getItem("idToken");
    const accessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");

    if (idToken && accessToken && refreshToken) {
      const cognitoUser = new CognitoUser({
        Username: this.getUsernameFromToken(idToken),
        Pool: this.userPool,
      });

      const session = new CognitoUserSession({
        IdToken: new CognitoIdToken({ IdToken: idToken }),
        AccessToken: new CognitoAccessToken({ AccessToken: accessToken }),
        RefreshToken: new CognitoRefreshToken({ RefreshToken: refreshToken }),
      });

      cognitoUser.setSignInUserSession(session);
      return cognitoUser;
    }

    return null;
  }

  getUserFromCognito(cognitoUser: CognitoUser, session: any, attributes: CognitoUserAttribute[]): User {
    let user: User = {};
    attributes.forEach((attribute) => {
      if (attribute.getName() !== "preferred_username" && attribute.getName() !== "email_verified") {
        const keyString = attribute.getName();
        user[keyString] = attribute.getValue();
      }
    });
    user.username = cognitoUser.getUsername();
    user.preferred_username = user.username;
    user.document = user.username;
    user.id = user.sub;
    localStorage.setItem("accessToken", session.getAccessToken().getJwtToken());
    return user;
  }
}
